import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';
import Table from '../../../../components/LeagueTable';
import FallbackContentAnimation from '../../../../components/FallbackContentAnimation';
import { LeagueTeamsPreloader } from '../../../../components/ContentPreloaders';

import LeagueLegend from '../LeagueLegend';
import { StyledContainer, StyledTableContainer } from './styled';

import { leagueSetTeamInfo } from '../../../../redux/actions/League';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import { showMobileSidebarSection } from '../../../../redux/actions/Team';

import { calendarData } from '../../../../redux/selector/League';

import { goalsForTeam } from '../../../../service/leagueTable';
import { POPUPS } from '../../../../service/constants';

const TableContainer = ({
  leagueId,
  table,
  selectedLeague,
  leagueName,
}) => {
  // REDUX
  const { TEAM_INFO_POPUP } = POPUPS;
  const dispatch = useDispatch();

  const fixtures = useSelector(calendarData);

  const crestByTeamId = useMemo(() => {
    const map = {};
    const days = Array.isArray(fixtures?.matches) ? fixtures.matches : [];
    days.forEach((day) => {
      (day?.matches || []).forEach((match) => {
        if (match?.home_team?._id && match.home_team.symbol) {
          map[String(match.home_team._id)] = match.home_team.symbol;
        }
        if (match?.away_team?._id && match.away_team.symbol) {
          map[String(match.away_team._id)] = match.away_team.symbol;
        }
      });
    });
    return map;
  }, [fixtures]);

  const data = useMemo(
    () =>
      table &&
      table.length > 0 &&
      table.map((tab, index) => ({
        ...tab,
        number: index + 1,
        symbol:
          tab.symbol ||
          (tab._id != null ? crestByTeamId[String(tab._id)] : null),
        ...goalsForTeam(tab, fixtures?.matches),
      })),
    [table, fixtures, crestByTeamId]
  );
  const columns = useMemo(
    () => [
      { Header: 'POS', accessor: 'number' },
      { Header: 'Team', accessor: 'name' },
      { Header: 'W', accessor: 'win' },
      { Header: 'D', accessor: 'draw' },
      { Header: 'L', accessor: 'loss' },
      { Header: 'GF', accessor: 'gf' },
      { Header: 'GA', accessor: 'ga' },
      { Header: 'GD', accessor: 'gd' },
      { Header: 'PTS', accessor: 'points' },
    ],
    []
  );
  const addInfoAboutTeam = (teamData) => {
    dispatch(leagueSetTeamInfo(teamData, isMobileOnly));
    if (isMobileOnly) {
      dispatch(showMobileSidebarSection('teamInfo'));
    } else {
      dispatch(showPopupWindow(TEAM_INFO_POPUP, TEAM_INFO_POPUP, true));
    }
  };

  const isTeamInCurrentLeague = leagueId === selectedLeague;
  return (
    <StyledTableContainer>
      <FallbackContentAnimation
        loading={!data || data.length === 0}
        repeat="10"
        fallback={<LeagueTeamsPreloader />}
      >
        <StyledContainer>
          {data && data.length > 0 && (
            <>
              <Table
                isTeamInCurrentLeague={isTeamInCurrentLeague}
                getTeamInfo={addInfoAboutTeam}
                data={data}
                columns={columns}
                canSelectPlayer
                leagueName={leagueName}
              />
              <LeagueLegend leagueName={leagueName} />
            </>
          )}
        </StyledContainer>
      </FallbackContentAnimation>
    </StyledTableContainer>
  );
};
TableContainer.propTypes = {
  leagueId: PropTypes.string.isRequired,
  table: PropTypes.array,
  selectedLeague: PropTypes.string,
  leagueName: PropTypes.string,
};
TableContainer.defaultProps = {
  table: [],
  selectedLeague: '',
  leagueName: null,
};
export default React.memo(TableContainer);
